import { SelectionChangedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { ScenarioMetadata } from "in_queue/types/scenarioType";
import {
    ScenarioSelectionMode,
    scenarioSelectionRules
} from "in_queue/utils/scenarioSelection";
import { RefObject, useCallback, useEffect, useMemo, useRef } from "react";

export const useScenarioTableSelection = ({
    mode,
    gridRef,
    onSelectedScenariosChange
}: {
    mode?: ScenarioSelectionMode;
    gridRef: RefObject<AgGridReact>;
    onSelectedScenariosChange?: (
        selected: ReadonlyArray<ScenarioMetadata>
    ) => void;
}): ((event: SelectionChangedEvent<ScenarioMetadata>) => void) => {
    const selectionLimit = useMemo(
        () =>
            mode === undefined
                ? undefined
                : scenarioSelectionRules(mode).selectionLimit,
        [mode]
    );
    const selectionOrderRef = useRef<string[]>([]);

    // Reset selections when the switching between
    // "Compare scenarios" and "Archive scenarios" modes,
    // or when scenarios are reloaded.
    useEffect(() => {
        selectionOrderRef.current = [];
        gridRef.current?.api?.deselectAll();
    }, [mode, gridRef]);

    const onSelectionChanged = useCallback(
        (event: SelectionChangedEvent<ScenarioMetadata>) => {
            const selected = event.api.getSelectedRows();
            const selectedIds = new Set(selected.map((s) => s.scenarioId));
            selectionOrderRef.current = selectionOrderRef.current.filter((id) =>
                selectedIds.has(id)
            );
            for (const row of selected) {
                if (!selectionOrderRef.current.includes(row.scenarioId)) {
                    selectionOrderRef.current.push(row.scenarioId);
                }
            }

            // if there's a selection limit, evict the oldest selections until we're under the limit
            // "Archive scenarios" does not have a selection limit, so this is not relevant.
            if (selectionLimit !== undefined) {
                while (selectionOrderRef.current.length > selectionLimit) {
                    const evictedId = selectionOrderRef.current.splice(
                        selectionOrderRef.current.length - selectionLimit,
                        1
                    )[0];
                    if (evictedId) {
                        event.api.getRowNode(evictedId)?.setSelected(false);
                    }
                }
            }
            onSelectedScenariosChange?.(event.api.getSelectedRows());
        },
        [selectionLimit, onSelectedScenariosChange]
    );

    return onSelectionChanged;
};
